/**
 * Deduplicação: mesclar cadastros e refazer vínculos. Duas frentes: o **merge manual** (escolha
 * duas pessoas e qual sobrevive) e a **revisão dos pares prováveis** que o monitor detecta. Em
 * ambos, fundir move TODOS os vínculos ao sobrevivente e apaga o absorvido — sem perder nada, e
 * registrado em auditoria (com snapshot do absorvido).
 */
import { ArrowLeftRight, Check, Copy, Eye, GitMerge, Search, Wand2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ConfirmarAcao } from '@/components/ConfirmarAcao';
import { ModalCompararPar } from '@/components/dedup/ModalCompararPar';
import {
  ComboBuscaEntidade,
  type ConfigBusca,
  type ItemBusca,
} from '@/components/crud/ComboBuscaEntidade';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { formatar } from '@/lib/mascaras';

type Candidata = {
  id: string;
  tipo: string;
  score: number;
  sinais: { sinais?: string[]; nivel?: string };
  a: { id: string; nome: string | null };
  b: { id: string; nome: string | null };
};

/** Como cada nível da régua aparece: o que sustenta o par, em uma palavra. */
const NIVEL: Record<string, { rotulo: string; classe: string }> = {
  confirmada: { rotulo: 'Confirmada', classe: 'bg-accent/15 text-accent' },
  provavel: { rotulo: 'Provável', classe: 'bg-surface-2 text-text-secondary' },
  homonimo: { rotulo: 'Homônimo', classe: 'bg-surface-2 text-text-tertiary' },
};

const BUSCA_PESSOA: ConfigBusca = {
  endpoint: '/cadastro/pessoas',
  rotulo: (p) => String(p.nome ?? ''),
  sub: (p) => (p.cpf ? formatar(String(p.cpf), 'cpf') : null),
  placeholder: 'Buscar pessoa por nome ou CPF…',
  camposRapido: [{ chave: 'nome', rotulo: 'Nome', tipo: 'texto', obrigatorio: true }],
};

/** Rótulo humano de uma pessoa escolhida na busca: nome + CPF quando houver. */
function rotuloPessoa(p: ItemBusca): string {
  const cpf = p.cpf ? ` · ${formatar(String(p.cpf), 'cpf')}` : '';
  return `${String(p.nome ?? p.id)}${cpf}`;
}

/** Funde duas pessoas pelo id (sobrevivente mantém tudo; absorvido some). Devolve ok. */
async function fundirPessoas(sobreviventeId: string, absorvidoId: string): Promise<boolean> {
  const r = await api.post('/cadastro/dedup/fundir', {
    sobrevivente_id: sobreviventeId,
    absorvido_id: absorvidoId,
  });
  if (!r.ok) {
    toast.error(r.erro);
    return false;
  }
  toast.success('Cadastros mesclados — vínculos refeitos.');
  return true;
}

/**
 * Merge manual: escolha quem MANTER e quem ABSORVER; ao fundir, os vínculos do absorvido passam
 * ao mantido e o absorvido é apagado. Cada busca exclui a pessoa já escolhida na outra.
 */
function MesclarManual({ onFundido }: { onFundido: () => void }) {
  const [manter, setManter] = useState<ItemBusca | null>(null);
  const [absorver, setAbsorver] = useState<ItemBusca | null>(null);
  const [ocupado, setOcupado] = useState(false);

  function trocar() {
    setManter(absorver);
    setAbsorver(manter);
  }

  async function fundir() {
    if (!manter || !absorver || ocupado) return;
    setOcupado(true);
    const ok = await fundirPessoas(String(manter.id), String(absorver.id));
    setOcupado(false);
    if (!ok) return;
    setManter(null);
    setAbsorver(null);
    onFundido();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <GitMerge className="size-4 text-accent" />
          Mesclar manualmente
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="tipo-corpo-pequeno text-text-tertiary">
          Escolha dois cadastros da mesma pessoa. O <b>mantido</b> herda os vínculos; o{' '}
          <b>absorvido</b> é apagado. A ação é auditada e guarda um retrato do absorvido.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_1fr] sm:items-end">
          <label className="flex flex-col gap-1">
            <span className="tipo-rotulo text-accent">Manter (sobrevive)</span>
            <ComboBuscaEntidade
              config={BUSCA_PESSOA}
              onSelecionar={setManter}
              excluirId={absorver ? String(absorver.id) : undefined}
            />
          </label>
          <Button
            type="button"
            variant="fantasma"
            onClick={trocar}
            disabled={!manter && !absorver}
            aria-label="Inverter mantido e absorvido"
            title="Inverter"
            className="mb-1 hidden sm:inline-flex"
          >
            <ArrowLeftRight />
          </Button>
          <label className="flex flex-col gap-1">
            <span className="tipo-rotulo text-danger">Absorver (apaga)</span>
            <ComboBuscaEntidade
              config={BUSCA_PESSOA}
              onSelecionar={setAbsorver}
              excluirId={manter ? String(manter.id) : undefined}
            />
          </label>
        </div>
        {manter && absorver && (
          <div className="rounded-md border border-border bg-surface-2 px-3 py-2 tipo-corpo-pequeno text-text-secondary">
            Vou manter <b className="text-text">{rotuloPessoa(manter)}</b> e apagar{' '}
            <b className="text-text">{rotuloPessoa(absorver)}</b>, movendo os vínculos dele.
          </div>
        )}
        <div className="flex justify-end">
          <Button
            variant="primario"
            onClick={fundir}
            disabled={!manter || !absorver || ocupado}
          >
            {ocupado ? <Spinner /> : <GitMerge />}
            Mesclar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/** Quantos pares a tela carrega por vez — o servidor lista paginado (a base tem milhares). */
const POR_PAGINA = 200;

export function Duplicatas() {
  const [candidatas, setCandidatas] = useState<Candidata[]>([]);
  const [total, setTotal] = useState(0);
  const [ocupado, setOcupado] = useState(false);
  // Ids de candidatas com direção invertida (b sobrevive em vez de a).
  const [invertidos, setInvertidos] = useState<Set<string>>(new Set());
  // Pares marcados para a fusão em lote.
  const [selecao, setSelecao] = useState<Set<string>>(new Set());
  // Lote aguardando confirmação: os selecionados ou todos os pendentes da base.
  const [lote, setLote] = useState<'selecao' | 'todas' | null>(null);
  // Par aberto no modal de comparação (id da candidata).
  const [comparando, setComparando] = useState<string | null>(null);

  async function carregar() {
    const r = await api.get<{ total: number; itens: Candidata[] }>(
      `/cadastro/dedup/candidatas?limite=${POR_PAGINA}`,
    );
    if (!r.ok) return;
    setCandidatas(r.dados.itens);
    setTotal(r.dados.total);
    setSelecao(new Set());
  }

  function inverter(id: string) {
    setInvertidos((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }

  function alternar(id: string) {
    setSelecao((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }

  /** Marca/desmarca todos os pares CARREGADOS (o "todas as N" é um botão à parte). */
  function alternarTodos() {
    setSelecao((s) =>
      s.size === candidatas.length ? new Set() : new Set(candidatas.map((c) => c.id)),
    );
  }

  /** Corpo da fusão em lote: os pares e a direção que o operador escolheu em cada um. */
  function itensDe(ids: string[]) {
    return ids.map((id) => ({ candidata_id: id, inverter: invertidos.has(id) }));
  }

  async function fundirLote() {
    if (ocupado || lote === null) return;
    const todas = lote === 'todas';
    // Em "todas" quem manda a lista é o servidor; só vão os ids invertidos, para ele saber a
    // direção dos pares em que o operador virou a seta.
    const itens = itensDe(todas ? [...invertidos] : [...selecao]);
    setOcupado(true);
    const r = await api.post<{
      enfileirado: boolean;
      fundidas?: number;
      ignoradas?: number;
    }>('/cadastro/dedup/fundir-lote', { itens, todas });
    setOcupado(false);
    setLote(null);
    if (!r.ok) return void toast.error(r.erro);
    if (r.dados.enfileirado) {
      toast.success('Fusão em lote enfileirada — acompanhe em Jobs.');
      return;
    }
    const ign = r.dados.ignoradas ? ` ${r.dados.ignoradas} ignorado(s) — já unidos no lote.` : '';
    toast.success(`${r.dados.fundidas} par(es) fundido(s).${ign}`);
    void carregar();
  }

  useEffect(() => {
    void carregar();
  }, []);

  async function detectar() {
    setOcupado(true);
    const r = await api.post<{ enfileirado: boolean }>('/cadastro/dedup/detectar');
    setOcupado(false);
    if (!r.ok) return void toast.error(r.erro);
    toast.success('Detecção enfileirada — acompanhe em Jobs. Os pares aparecem ao concluir.');
  }

  async function fundirSeguros() {
    if (ocupado) return;
    setOcupado(true);
    const r = await api.post<{ fundidas: number; absorvidos: number }>(
      '/cadastro/dedup/fundir-automatico',
    );
    setOcupado(false);
    if (!r.ok) return void toast.error(r.erro);
    toast.success(
      r.dados.fundidas > 0
        ? `${r.dados.fundidas} pares de alta confiança fundidos. Os demais ficam para revisão.`
        : 'Nenhum par seguro para fundir agora. Rode "Detectar" antes.',
    );
    void carregar();
  }

  async function fundir(c: Candidata) {
    if (ocupado) return;
    // Por padrão `a` sobrevive; invertido, `b`. O operador decide antes de fundir.
    const [sobrevivente, absorvido] = invertidos.has(c.id) ? [c.b, c.a] : [c.a, c.b];
    setOcupado(true);
    const ok = await fundirPessoas(sobrevivente.id, absorvido.id);
    setOcupado(false);
    if (!ok) return;
    setComparando(null);
    void carregar();
  }

  /** Funde o par aberto no modal de comparação, na direção que estiver escolhida lá. */
  async function fundirComparado() {
    const c = candidatas.find((x) => x.id === comparando);
    if (c) await fundir(c);
  }

  async function rejeitar(c: Candidata) {
    const r = await api.post(`/cadastro/dedup/candidatas/${c.id}/rejeitar`);
    if (!r.ok) return void toast.error(r.erro);
    void carregar();
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <header className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Copy className="size-5 text-accent" />
            <h1 className="tipo-titulo">Duplicatas</h1>
          </div>
          <p className="mt-1 tipo-corpo-grande text-text-secondary">
            Pares prováveis da mesma pessoa. Funda (une sem perder vínculo) ou rejeite.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            variant="secundario"
            onClick={fundirSeguros}
            disabled={ocupado}
            title="Funde em lote só os pares de ALTA confiança: nome idêntico + corroboração (telefone/e-mail/nascimento/nome da mãe). O resto fica para revisão."
          >
            {ocupado ? <Spinner /> : <Wand2 />}
            Fundir seguros
          </Button>
          <Button variant="primario" onClick={detectar} disabled={ocupado}>
            {ocupado ? <Spinner /> : <Search />}
            Detectar
          </Button>
        </div>
      </header>

      <MesclarManual onFundido={carregar} />

      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="tipo-subtitulo text-text-secondary">
            Pares prováveis {total > 0 && `(${total})`}
            {total > candidatas.length && (
              <span className="ml-2 tipo-corpo-pequeno font-normal text-text-tertiary">
                mostrando os {candidatas.length} mais prováveis
              </span>
            )}
          </h2>
          {candidatas.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex cursor-pointer items-center gap-2 tipo-corpo-pequeno text-text-secondary">
                <input
                  type="checkbox"
                  checked={selecao.size === candidatas.length}
                  onChange={alternarTodos}
                  className="size-4 accent-primary"
                />
                Selecionar os {candidatas.length} exibidos
              </label>
              <Button
                variant="secundario"
                onClick={() => setLote('selecao')}
                disabled={ocupado || selecao.size === 0}
              >
                <GitMerge />
                Fundir selecionados ({selecao.size})
              </Button>
              {total > candidatas.length && (
                <Button
                  variant="secundario"
                  onClick={() => setLote('todas')}
                  disabled={ocupado}
                  title="Funde TODOS os pares pendentes da base, não só os exibidos. Vai para a fila e você acompanha em Jobs."
                >
                  <GitMerge />
                  Fundir todos os {total}
                </Button>
              )}
            </div>
          )}
        </div>
        {candidatas.length === 0 ? (
          <Card>
            <CardContent className="grid min-h-24 place-items-center py-8 text-center">
              <p className="tipo-corpo text-text-tertiary">
                Nenhuma duplicata pendente. Rode "Detectar" após cadastrar/ingerir.
              </p>
            </CardContent>
          </Card>
        ) : (
          candidatas.map((c) => {
            const [sobrev, absorv] = invertidos.has(c.id) ? [c.b, c.a] : [c.a, c.b];
            return (
              <Card key={c.id} className={selecao.has(c.id) ? 'border-accent' : undefined}>
                <CardContent className="flex flex-col gap-3 py-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selecao.has(c.id)}
                      onChange={() => alternar(c.id)}
                      aria-label={`Selecionar par ${sobrev.nome ?? ''} / ${absorv.nome ?? ''}`}
                      className="size-4 accent-primary"
                    />
                    <span className="tipo-dado text-accent" title="Mantido">
                      {sobrev.nome ?? sobrev.id.slice(0, 8)}
                    </span>
                    <button
                      type="button"
                      onClick={() => inverter(c.id)}
                      aria-label="Inverter qual sobrevive"
                      title="Inverter"
                      className="rounded-sm p-1 text-text-tertiary hover:text-text"
                    >
                      <ArrowLeftRight className="size-4" />
                    </button>
                    <span className="tipo-dado text-text-tertiary line-through" title="Absorvido">
                      {absorv.nome ?? absorv.id.slice(0, 8)}
                    </span>
                    {c.sinais.nivel && NIVEL[c.sinais.nivel] && (
                      <span
                        className={`ml-2 rounded px-2 py-0.5 tipo-corpo-pequeno ${NIVEL[c.sinais.nivel].classe}`}
                      >
                        {NIVEL[c.sinais.nivel].rotulo}
                      </span>
                    )}
                    <span className="rounded bg-surface-2 px-2 py-0.5 tipo-corpo-pequeno text-text-tertiary">
                      {(c.sinais.sinais ?? []).join(' · ')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="tipo-corpo-pequeno text-text-tertiary">
                      Mantém <b className="text-text-secondary">{sobrev.nome ?? '—'}</b>
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="fantasma"
                        onClick={() => setComparando(c.id)}
                        title="Ver os dois cadastros lado a lado — campos, contatos, relações e demais vínculos"
                      >
                        <Eye />
                        Comparar
                      </Button>
                      <Button variant="secundario" onClick={() => rejeitar(c)}>
                        <X />
                        Rejeitar
                      </Button>
                      <Button variant="primario" onClick={() => fundir(c)} disabled={ocupado}>
                        <Check />
                        Fundir
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      <ConfirmarAcao
        aberto={lote !== null}
        onAbrir={(v) => !v && setLote(null)}
        titulo="Fundir pares em lote"
        impacto={
          <>
            Vou fundir <b className="text-text">{lote === 'todas' ? total : selecao.size}</b>{' '}
            {(lote === 'todas' ? total : selecao.size) === 1 ? 'par' : 'pares'}: em cada um, o
            cadastro <b className="text-text">mantido</b> herda os vínculos do{' '}
            <b className="text-text">absorvido</b>, que é apagado. Cada fusão fica na auditoria com
            o retrato do absorvido.
            {lote === 'todas' && (
              <> Como é a base inteira, vai para a fila — acompanhe em Jobs.</>
            )}
          </>
        }
        rotuloConfirmar="Fundir"
        ocupado={ocupado}
        onConfirmar={fundirLote}
      />

      <ModalCompararPar
        candidataId={comparando}
        invertido={comparando !== null && invertidos.has(comparando)}
        onInverter={() => comparando !== null && inverter(comparando)}
        onFechar={() => setComparando(null)}
        onFundir={fundirComparado}
        ocupado={ocupado}
      />
    </div>
  );
}
